import { useEffect, useState } from "react";
import { CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePageMeta } from "@/hooks/usePageMeta";
import { api } from "@/lib/api";
import styles from "./VerifyEmail.module.css";

type VerifyResponse = { verified: true; shortcut: string; name: string };

export function VerifyEmail() {
  const { shortcut = "", token = "" } = useParams();
  const { t } = useTranslation();
  const [state, setState] = useState<"pending" | "ok" | "fail">("pending");
  const [eventName, setEventName] = useState<string | null>(null);

  usePageMeta({
    title: t("verify.checking").replace(/…$/, ""),
    robots: "noindex,nofollow",
  });

  useEffect(() => {
    let cancelled = false;
    api
      .get<VerifyResponse>(
        `/api/v1/events/${encodeURIComponent(shortcut)}/verify-email/${encodeURIComponent(token)}`,
      )
      .then((res) => {
        if (cancelled) return;
        setEventName(res.name);
        setState("ok");
      })
      .catch(() => {
        if (!cancelled) setState("fail");
      });
    return () => {
      cancelled = true;
    };
  }, [shortcut, token]);

  if (state === "pending") {
    return <p className={styles.status}>{t("verify.checking")}</p>;
  }
  if (state === "fail") {
    return (
      <section className={styles.page}>
        <div className={`${styles.badge} ${styles.badgeFail}`}>
          <WarningCircle size={32} weight="regular" />
        </div>
        <h1>{t("verify.failTitle")}</h1>
        <p>{t("verify.failBody")}</p>
        <Link to="/" className={styles.homeLink}>
          {t("verify.goHome")}
        </Link>
      </section>
    );
  }
  return (
    <section className={styles.page}>
      <div className={`${styles.badge} ${styles.badgeOk}`}>
        <CheckCircle size={32} weight="regular" />
      </div>
      <h1>{t("verify.okTitle")}</h1>
      <p>{t("verify.okBody", { name: eventName })}</p>
      <Link to="/" className={styles.homeLink}>
        {t("verify.goHome")}
      </Link>
    </section>
  );
}
